import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  MenuItem,
  Pagination,
  Divider
} from "@mui/material";
import { Add, Edit, DeleteOutline } from "@mui/icons-material";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDoc
} from "firebase/firestore";
import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import Swal from "sweetalert2";

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505'
};

function Extraservices() {

  /* ================= STATE ================= */
  const [userData, setUserData] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [plans, setPlans] = useState([]);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 5;

  const [form, setForm] = useState({
    servicename: "",
    amount: "",
    status: "active"
  });

  /* ================= AUTH ================= */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, user => setUserData(user));
    return () => unsub();
  }, []);

  /* ================= FETCH PROPERTY ================= */
  useEffect(() => {
    const fetchRole = async () => {
      if (!userData) return;

      const roleRef = doc(db, "authroles", userData.uid);
      const snap = await getDoc(roleRef);

      if (snap.exists()) {
        setPropertyId(snap.data().propertyId || null);
      } else {
        Swal.fire("Error", "Property not assigned", "error");
      }
    };

    fetchRole();
  }, [userData]);

  /* ================= FETCH SERVICES ================= */
  const fetchPlans = async () => {
    if (!propertyId) return;

    const q = query(
      collection(db, "extraservices"),
      where("propertyId", "==", propertyId)
    );

    const snap = await getDocs(q);
    setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchPlans();
  }, [propertyId]);

  /* ================= SAVE ================= */
  const savePlan = async () => {
    if (!form.servicename || !form.amount) {
      Swal.fire("Required", "Service name & amount required", "warning");
      return;
    }

    try {
      if (editId) {
        await updateDoc(doc(db, "extraservices", editId), {
          ...form,
          amount: Number(form.amount)
        });
        Swal.fire("Updated", "Extra Service updated", "success");
      } else {
        await addDoc(collection(db, "extraservices"), {
          ...form,
          amount: Number(form.amount),
          propertyId,
          createdAt: serverTimestamp()
        });
        Swal.fire("Added", "Extra Service added", "success");
      }

      setOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  /* ================= DELETE ================= */
  const deletePlan = async (id) => {
    const ok = await Swal.fire({
      title: "Delete Extra Service?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      background: colors.calPolyGreen,
      color: colors.vanilla,
      confirmButtonColor: colors.goldenrod,
      cancelButtonColor: colors.darkGreen
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "extraservices", id));
      Swal.fire("Deleted", "Extra Service removed", "success");
      fetchPlans();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setEditId(null);
    setForm({ servicename: "", amount: "", status: "active" });
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      servicename: plan.servicename,
      amount: plan.amount,
      status: plan.status
    });
    setOpen(true);
  };

  /* ================= FILTER + PAGINATION ================= */
  const filteredPlans = useMemo(() => {
    return plans.filter(p =>
      p.servicename?.toLowerCase().includes(search.toLowerCase())
    );
  }, [plans, search]);

  const totalPages = Math.ceil(filteredPlans.length / rowsPerPage);

  const paginatedPlans = filteredPlans.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  /* ================= UI ================= */
  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
      <Box component="main" sx={{ flexGrow: 1, maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
        
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography fontSize={26} fontWeight={800} sx={{ color: colors.vanilla }}>
            Extra Services
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
            disabled={!propertyId}
            sx={{
              borderRadius: "10px",
              px: 3,
              py: 1,
              fontWeight: 700,
              textTransform: "none",
              bgcolor: colors.goldenrod,
              color: colors.darkGreen,
              boxShadow: "0 4px 12px rgba(209, 165, 5, 0.2)",
              "&:hover": { bgcolor: colors.gold }
            }}
          >
            Add Extra Service
          </Button>
        </Stack>

        {/* SEARCH */}
        <TextField
          placeholder="Search Service..."
          size="small"
          fullWidth
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ style: { color: colors.vanilla } }}
          InputProps={{ style: { color: colors.vanilla } }}
          sx={{ 
            mb: 3, 
            maxWidth: 300,
            "& .MuiOutlinedInput-root": { 
              borderRadius: "8px",
              backgroundColor: colors.calPolyGreen,
              "& fieldset": { borderColor: colors.goldenrod },
              "&.Mui-focused fieldset": { borderColor: colors.gold }
            } 
          }}
        />

        <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
          <CardContent sx={{ p: 3 }}>
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: colors.darkGreen }}>
                  <TableRow>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr No</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Service Name</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Amount (₹)</TableCell>
                    <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                    <TableCell align="right" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedPlans.map((p, index) => (
                    <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        {(page - 1) * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{p.servicename}</TableCell>
                      <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>₹ {p.amount}</TableCell>
                      <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        <Chip
                          label={p.status.toUpperCase()}
                          color={p.status === "active" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                        <IconButton sx={{ color: colors.gold }} onClick={() => openEdit(p)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton color="error" onClick={() => deletePlan(p.id)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!paginatedPlans.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                        No Extra Services found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* PAGINATION */}
            {totalPages > 1 && (
              <Stack alignItems="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                  size="small"
                />
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* DIALOG */}
        <Dialog 
          open={open} 
          onClose={() => setOpen(false)} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow: "0 24px 50px rgba(0,0,0,0.5)",
              border: `1px solid ${colors.goldenrod}`,
              bgcolor: colors.calPolyGreen,
              color: colors.vanilla,
              overflow: "hidden"
            }
          }}
        >
          <DialogTitle sx={{ bgcolor: colors.darkGreen, color: colors.vanilla, fontWeight: 700, py: 2 }}>
            {editId ? "Edit Extra Service" : "Add Extra Service"}
          </DialogTitle>
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

          <DialogContent dividers sx={{ borderColor: colors.goldenrod, opacity: 0.9, p: 3, bgcolor: colors.calPolyGreen }}>
            <Stack spacing={2.5}>
              <TextField
                label="Service Name"
                value={form.servicename}
                onChange={(e) =>
                  setForm({ ...form, servicename: e.target.value })
                }
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  } 
                }}
              />

              <TextField
                label="Amount"
                type="number"
                value={form.amount}
                onChange={(e) =>
                  setForm({ ...form, amount: e.target.value })
                }
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                InputProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    backgroundColor: colors.darkGreen,
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  } 
                }}
              />

              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value })
                }
                fullWidth
                size="small"
                InputLabelProps={{ style: { color: colors.vanilla } }}
                sx={{ 
                  color: colors.vanilla,
                  backgroundColor: colors.darkGreen,
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-root": { 
                    borderRadius: "8px",
                    "& fieldset": { borderColor: colors.goldenrod },
                    "&:hover fieldset": { borderColor: colors.gold },
                    "&.Mui-focused fieldset": { borderColor: colors.gold }
                  },
                  '& .MuiSelect-select': { color: colors.vanilla },
                  '& .MuiSvgIcon-root': { color: colors.vanilla }
                }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <Divider sx={{ borderColor: colors.goldenrod, opacity: 0.4 }} />

          <DialogActions sx={{ p: 2.5, bgcolor: colors.darkGreen, gap: 1 }}>
            <Button onClick={() => setOpen(false)} sx={{ color: colors.vanilla, textTransform: "none", fontWeight: 700 }}>Cancel</Button>
            <Button 
              variant="contained" 
              onClick={savePlan}
              sx={{
                bgcolor: colors.goldenrod, 
                color: colors.darkGreen, 
                fontWeight: 700, 
                textTransform: "none", 
                borderRadius: "8px",
                '&:hover': { bgcolor: colors.gold }
              }}
            >
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </Box>
  );
}

export default Extraservices;